import type {
    AddCommerceCartLineRequest,
    ApplyCommerceCartCouponRequest,
    CommerceCart,
    CommerceCartCoupon,
    CommerceCartLine,
    CommerceCartPurchaseType,
    CommerceMoney,
    RemoveCommerceCartCouponRequest,
    RemoveCommerceCartLineRequest,
    UpdateCommerceCartLineQuantityRequest,
} from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import { addCommerceCartLineOperation } from "../operations/add-commerce-cart-line-operation.js";
import { applyCommerceCartCouponOperation } from "../operations/apply-commerce-cart-coupon-operation.js";
import { clearCommerceCartOperation } from "../operations/clear-commerce-cart-operation.js";
import { getCommerceCartOperation } from "../operations/get-commerce-cart-operation.js";
import { removeCommerceCartCouponOperation } from "../operations/remove-commerce-cart-coupon-operation.js";
import { removeCommerceCartLineOperation } from "../operations/remove-commerce-cart-line-operation.js";
import { updateCommerceCartLineQuantityOperation } from "../operations/update-commerce-cart-line-quantity-operation.js";
import { mockProducts } from "../products/mock-product-catalogue.js";

export interface DefaultCartController {
    readonly current: () => CommerceCart;
    readonly replace: (
        lines: readonly CommerceCartLine[],
        replacementVersion?: number,
    ) => CommerceCart;
    readonly applySharedItems: (
        items: readonly AddCommerceCartLineRequest[],
        strategy: "merge" | "replace",
    ) => CommerceCart;
}

export function registerDefaultCartOperations(
    transport: MockCommerceTransport,
): DefaultCartController {
    let version = 0;
    let lines: CommerceCartLine[] = [];
    let couponCodes: string[] = [];

    const current = (): CommerceCart => buildCart(version, lines, couponCodes);
    const mutate = (expectedVersion: number | undefined, action: () => void): CommerceCart => {
        if (expectedVersion !== undefined && expectedVersion !== version)
            throw new Error(
                `Cart version conflict. Expected ${expectedVersion}, current ${version}.`,
            );
        action();
        version += 1;
        return current();
    };

    transport.register(getCommerceCartOperation, () => ({ cart: current() }));
    transport.register(addCommerceCartLineOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            lines = addLine(lines, request);
        }),
    }));
    transport.register(updateCommerceCartLineQuantityOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            lines = updateLine(lines, request);
        }),
    }));
    transport.register(removeCommerceCartLineOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            lines = removeLine(lines, request);
        }),
    }));
    transport.register(clearCommerceCartOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            lines = [];
            couponCodes = [];
        }),
    }));
    transport.register(applyCommerceCartCouponOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            couponCodes = applyCoupon(couponCodes, request);
        }),
    }));
    transport.register(removeCommerceCartCouponOperation, (request) => ({
        cart: mutate(request.expectedVersion, () => {
            couponCodes = removeCoupon(couponCodes, request);
        }),
    }));

    const applySharedItems = (
        items: readonly AddCommerceCartLineRequest[],
        strategy: "merge" | "replace",
    ): CommerceCart =>
        mutate(undefined, () => {
            if (strategy === "replace") {
                lines = [];
                couponCodes = [];
            }
            for (const item of items) lines = addLine(lines, item);
        });

    const replace = (
        nextLines: readonly CommerceCartLine[],
        replacementVersion?: number,
    ): CommerceCart => {
        lines = [...nextLines];
        couponCodes = [];
        version = replacementVersion ?? version + 1;
        return current();
    };

    return Object.freeze({ current, replace, applySharedItems });
}

function addLine(
    lines: CommerceCartLine[],
    request: AddCommerceCartLineRequest,
): CommerceCartLine[] {
    const product = mockProducts.find((item) => item.id === request.productId);
    if (product === undefined) throw new Error(`Product '${request.productId}' was not found.`);
    const variant =
        request.variantId === undefined
            ? (product.variants.find((item) => item.id === product.defaultVariantId) ??
              product.variants[0])
            : product.variants.find((item) => item.id === request.variantId);
    if (variant === undefined) throw new Error(`Variant '${request.variantId}' was not found.`);
    const purchaseType: CommerceCartPurchaseType = request.purchaseType ?? "one-time";
    const offer = variant.offers.find((candidate) => candidate.purchaseType === purchaseType);
    if (offer === undefined)
        throw new Error(`Product '${product.id}' does not support ${purchaseType} purchases.`);
    const frequency =
        purchaseType === "recurring"
            ? request.frequencyId === undefined
                ? (offer.frequency?.options.find(
                      (option) => option.days === request.frequencyDays,
                  ) ??
                  offer.frequency?.options.find(
                      (option) => option.id === offer.frequency?.defaultOptionId,
                  ))
                : offer.frequency?.options.find((option) => option.id === request.frequencyId)
            : undefined;
    const frequencyDays = frequency?.days;
    if (purchaseType === "recurring" && offer.frequency !== undefined && frequency === undefined)
        throw new Error(
            `Product '${product.id}' does not support the requested recurring frequency.`,
        );
    const unit = offer.price.current;
    const id = `${product.id}:${variant.id}:${purchaseType}:${frequency?.id ?? "none"}`;
    const existing = lines.find((line) => line.id === id);
    if (existing !== undefined)
        return lines.map((line) =>
            line.id === id
                ? makeLine(
                      product.id,
                      variant.id,
                      variant.displaySku,
                      variant.name,
                      product.primaryImage,
                      purchaseType,
                      line.quantity + request.quantity,
                      unit,
                      frequencyDays,
                      frequency,
                      product.components,
                  )
                : line,
        );
    return [
        ...lines,
        makeLine(
            product.id,
            variant.id,
            variant.displaySku,
            variant.name,
            product.primaryImage,
            purchaseType,
            request.quantity,
            unit,
            frequencyDays,
            frequency,
            product.components,
        ),
    ];
}

function updateLine(
    lines: CommerceCartLine[],
    request: UpdateCommerceCartLineQuantityRequest,
): CommerceCartLine[] {
    if (!lines.some((line) => line.id === request.lineId))
        throw new Error(`Cart line '${request.lineId}' was not found.`);
    return lines.map((line) =>
        line.id === request.lineId
            ? makeLine(
                  line.productId,
                  line.variantId,
                  line.displaySku,
                  line.name,
                  line.image,
                  line.purchaseType,
                  request.quantity,
                  line.unitPrice,
                  line.frequencyDays,
                  line.frequency,
                  line.components,
                  line.id,
              )
            : line,
    );
}
function removeLine(
    lines: CommerceCartLine[],
    request: RemoveCommerceCartLineRequest,
): CommerceCartLine[] {
    if (!lines.some((line) => line.id === request.lineId))
        throw new Error(`Cart line '${request.lineId}' was not found.`);
    return lines.filter((line) => line.id !== request.lineId);
}
function applyCoupon(codes: string[], request: ApplyCommerceCartCouponRequest): string[] {
    const code = request.code.trim().toUpperCase();

    return codes.includes(code) ? codes : [...codes, code];
}
function removeCoupon(codes: string[], request: RemoveCommerceCartCouponRequest): string[] {
    return codes.filter((code) => code !== request.code);
}

function buildCart(
    version: number,
    lines: CommerceCartLine[],
    couponCodes: string[],
): CommerceCart {
    const subtotal = round(lines.reduce((sum, line) => sum + line.lineSubtotal.amount, 0));
    const coupons: CommerceCartCoupon[] = couponCodes.map((code) =>
        Object.freeze({
            code,
            description: "Applied coupon",
            discount: money(0),
        }),
    );

    return Object.freeze({
        id: "local-cart",
        version,
        currencyCode: "NZD",
        lines: Object.freeze([...lines]),
        coupons: Object.freeze(coupons),
        totals: Object.freeze({
            itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
            subtotal: money(subtotal),
            discount: money(0),
            shipping: money(0),
            tax: money(0),
            total: money(subtotal),
        }),
        updatedAt: new Date(),
    });
}
function makeLine(
    productId: string,
    variantId: string,
    displaySku: string | undefined,
    name: string,
    image: CommerceCartLine["image"],
    purchaseType: CommerceCartPurchaseType,
    quantity: number,
    unit: CommerceMoney,
    frequencyDays?: number,
    frequency?: CommerceCartLine["frequency"],
    components?: CommerceCartLine["components"],
    lineId?: string,
): CommerceCartLine {
    const subtotal = round(unit.amount * quantity);
    return Object.freeze({
        id: lineId ?? `${productId}:${variantId}:${purchaseType}:${frequency?.id ?? "none"}`,
        productId,
        variantId,
        ...(displaySku === undefined ? {} : { displaySku }),
        name,
        image,
        purchaseType,
        ...(frequency === undefined ? {} : { frequency }),
        ...(frequencyDays === undefined ? {} : { frequencyDays }),
        quantity,
        unitPrice: unit,
        lineSubtotal: money(subtotal),
        lineDiscount: money(0),
        lineTotal: money(subtotal),
        ...(components === undefined ? {} : { components }),
    });
}
function money(amount: number): CommerceMoney {
    return Object.freeze({
        amount,
        currencyCode: "NZD",
        formatted: `$${amount.toFixed(2)}`,
    });
}
function round(value: number): number {
    return Math.round(value * 100) / 100;
}
